import { useDefaultLocalizationTexts } from '@mews/framework';
import { getToken, ThemeVariant as DSThemeVariant, WithChildren } from '@optimus-web/core';
import { DENSE_B2B_DARK_THEME, DENSE_B2B_LIGHT_THEME, OptimusProvider } from '@optimus-web/theme';
import { getThemeOverrides } from '../../theme/theme';

export type BEThemeVariant = 'Light' | 'Dark';

interface ThemeMapping {
    variant: DSThemeVariant;
    themeObject: typeof DENSE_B2B_LIGHT_THEME | typeof DENSE_B2B_DARK_THEME;
}

/**
 * Maps backend theme variants to design system theme variants and theme objects
 */
const mapping: Record<BEThemeVariant, ThemeMapping> = {
    Light: {
        variant: 'b2bLight',
        themeObject: DENSE_B2B_LIGHT_THEME,
    },
    Dark: {
        variant: 'b2bDark',
        themeObject: DENSE_B2B_DARK_THEME,
    },
};
const mapBEThemeToDS = (beVariant: BEThemeVariant): ThemeMapping => mapping[beVariant] ?? mapping.Light;

export interface ThemeWrapperProps extends WithChildren {
    primaryColor: string | null;
    variant: BEThemeVariant;
}

export const ThemeWrapper: React.FC<ThemeWrapperProps> = ({ children, primaryColor, variant }) => {
    const { variant: dsVariant, themeObject } = mapBEThemeToDS(variant);
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    const overrides = primaryColor ? getThemeOverrides(primaryColor) : undefined;
    const localButtonOverrides =
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        primaryColor && overrides
            ? {
                  backgroundInteractivePrimaryDefault: primaryColor,
                  backgroundInteractivePrimaryActive: overrides.decisions.interactive.primary.active,
                  backgroundInteractivePrimaryHover: overrides.decisions.interactive.primary.hover,
                  borderInteractiveFocus: overrides?.decisions.borderColor.semantic.primary,
                  borderInteractivePrimaryDefault: overrides?.decisions.borderColor.semantic.primary,
                  textInteractivePrimaryDefault: overrides.decisions.interactive.primary.default,
                  borderInteractivePrimaryHover: overrides.decisions.interactive.primary.hover,
                  borderInteractivePrimaryActive: overrides.decisions.interactive.primary.active,
              }
            : undefined;

    return (
        <OptimusProvider
            tokenOverrides={{
                ...localButtonOverrides,
                backgroundStaticSunken: getToken('backgroundStaticFlat')({ theme: themeObject }),
            }}
            themeVariant={dsVariant}
            localizationTexts={useDefaultLocalizationTexts()}
        >
            {children}
        </OptimusProvider>
    );
};
